import { computed, Inject, Injectable, signal } from '@angular/core';
import { ISessionIdleConfig } from 'app/session-idle/interfaces/session-idle-config.interface';
import { SESSION_IDLE_CONFIG } from 'app/session-idle/tokens/session-config.token';
import { fromEvent, merge, startWith, Subject, Subscription, timer } from 'rxjs';
import { share, switchMap, takeUntil, tap } from 'rxjs/operators';
import { COUNTDOWN_START_KEY, LAST_ACTIVITY_KEY, LOGOUT_KEY } from './session-idle.constants';

@Injectable({ providedIn: 'root' })
export class SessionIdleService {
    private hasStarted = false;
    private stop$ = new Subject<void>();
    private subscription: Subscription | null = null;

    private _isIdle = signal(false);
    public isIdle = computed(() => this._isIdle());

    private _showWarning = signal(false);
    public showWarning = computed(() => this._showWarning());

    private _lastActivity = signal<number>(Date.now());
    public lastActivity = computed(() => this._lastActivity());

    public constructor(@Inject(SESSION_IDLE_CONFIG) private config: ISessionIdleConfig) {
        this.setupCrossTabListeners();
        this.setupVisibilityChangeListener();
        this.updateCountdownStart();
    }

    public start(): void {
        if (this.hasStarted) {
            return;
        }

        if (this.config.timeoutDurationMs <= 0 || this.config.warningDurationMs <= 0) {
            console.log('[SessionIdle] Skipped — timeout config is 0 or invalid');
            return;
        }

        this.hasStarted = true;
        this.simulateInitialActivity();

        const activity$ = merge(
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'touchstart'),
        ).pipe(
            tap(() => {
                this._isIdle.set(false);
                this._showWarning.set(false);
                this.updateLastActivity();
            }),
            share(),
        );

        const resettableTimer$ = activity$.pipe(
            startWith(0),
            switchMap(() =>
                timer(this.config.warningDurationMs).pipe(
                    tap(() => this._showWarning.set(true)),
                    switchMap(() =>
                        timer(this.config.timeoutDurationMs - this.config.warningDurationMs),
                    ),
                ),
            ),
        );

        this.subscription = resettableTimer$
            .pipe(takeUntil(this.stop$))
            .subscribe(() => {
                this._isIdle.set(true);
                this._showWarning.set(false);
                this.broadcastLogout();
            });
    }

    public stop(): void {
        this.stop$.next();
        this.subscription?.unsubscribe();
        this.hasStarted = false;

        queueMicrotask(() => {
            this._isIdle.set(false);
            this._showWarning.set(false);
            localStorage.removeItem(LAST_ACTIVITY_KEY);
            localStorage.removeItem(COUNTDOWN_START_KEY);
            localStorage.removeItem(LOGOUT_KEY);
        });
    }

    public reset(): void {
        this.stop();
        this.start();
    }

    public simulateInitialActivity(): void {
        queueMicrotask(() => {
            this._isIdle.set(false);
            this._showWarning.set(false);
            this.updateLastActivity();
        });
    }

    private updateLastActivity(): void {
        const now = Date.now();
        this._lastActivity.set(now);
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    }

    private broadcastLogout(): void {
        localStorage.setItem(LOGOUT_KEY, Date.now().toString());
    }

    private setupCrossTabListeners(): void {
        window.addEventListener('storage', (event) => {
            if (event.key === LAST_ACTIVITY_KEY) {
                const last = parseInt(event.newValue || '0', 10);
                if (last) {
                    this.reset(); // User is active in another tab
                }
            }

            if (event.key === LOGOUT_KEY) {
                // Another tab logged out
                this._isIdle.set(true);
            }
        });

        // Use BroadcastChannel for modern browsers
        if ('BroadcastChannel' in window) {
            const channel = new BroadcastChannel('session');

            channel.onmessage = (event) => {
                if (event.data === 'logout') {
                    this._isIdle.set(true);
                } else if (event.data === 'active') {
                    this.reset();
                }
            };
        }
    }

    private setupVisibilityChangeListener(): void {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.stop(); // Prevent double timers
                return;
            }

            // Tab becomes active
            const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
            const now = Date.now();
            const elapsed = now - last;

            if (elapsed > this.config.timeoutDurationMs && this.hasStarted) {
                this._isIdle.set(true);
                return;
            }

            this.reset(); // Keep the current session alive if still valid
        });
    }

    private updateCountdownStart(countdownStartKey: string = COUNTDOWN_START_KEY): void {
        if (this._isIdle() || !this.hasStarted) {
            return;
        }

        localStorage.setItem(countdownStartKey, Date.now().toString());
    }
}
